import BaseServiceException from "@/services/exceptions.ts";
import {captureSentryException} from "@/integrations/sentry/client.ts";
import {camelCaseObjectKeys, type CamelCaseKeys, snakeCaseObjectKeys, type SnakeCaseKeys} from "@/lib/utils.ts";
import {validateUUID} from "@validators";
import logger from "@logger";

/**
 * Base Service that contains common functionality used across other services
 */
abstract class BaseService {

    /**
     * Handles exceptions and pipes them through to an APM
     * @param {Error} err error encountered while handling an operation
     * @param {Record<string, unknown>} attributes optional attributes for the error
     */
    protected handleError = (err: Error, attributes?: Record<string, unknown>): Promise<BaseServiceException> => {
        logger.error(err.message, {...attributes, err})
        captureSentryException(err, null, err.message);
        return Promise.reject(err);
    };

    /**
     * Handles transformation on the request before sending it out
     * @param {Record<string, unknown>} payload a generic key value mapping of the request
     */
    protected convertRequestToSnakeCase<T extends object> (payload: T): SnakeCaseKeys<T> {
        return snakeCaseObjectKeys(payload);
    };

    /**
     * Handles responses from external service call and transforms it
     * @param {Record<string, unknown>} data generic response returned from external API call
     */
    protected convertToCamelCase = <T extends object>(data: T | T[]): CamelCaseKeys<T> | CamelCaseKeys<T>[] => {
        if (Array.isArray(data)) {
            return data.map((item) => camelCaseObjectKeys(item));
        }
        return camelCaseObjectKeys(data);
    };

    /**
     * Wrapper around validateUUID. Performs a basic UUID check
     * @param id
     */
    protected validateUUID = (id: string): void => {
        validateUUID(id);
    };
}

export default BaseService;
